import { useMemo, useState } from "react";
import axios from "axios";
import { adminErrorDetail } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" },
  { key: "no_show", label: "No show" },
  { key: "cancelled", label: "Cancelled" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.key, s.label]));
const STATUS_STYLE = {
  scheduled: { backgroundColor: "#2C4A63", color: "#FFFFFF" },
  completed: { backgroundColor: "#2F6B3F", color: "#FFFFFF" },
  no_show: { backgroundColor: "#9E2B20", color: "#FFFFFF" },
  cancelled: { backgroundColor: "#E3B23C", color: "#1B1917" },
};
const OPTION_STYLE = { backgroundColor: "#FFFFFF", color: "#1B1917" };

const FALLBACK_TZS = [
  "Europe/Berlin", "Europe/London", "Europe/Madrid", "Europe/Athens",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore",
  "Asia/Tokyo", "Australia/Sydney", "UTC",
];

const fmtDate = (ymd) => {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00Z`);
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });
};

const dayDelta = (m) => {
  if (!m.berlin_date || !m.meeting_date) return 0;
  const a = new Date(`${m.meeting_date}T00:00:00Z`).getTime();
  const b = new Date(`${m.berlin_date}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86400000);
};

const emptyForm = (tz) => ({
  email: "", meeting_date: "", meeting_time: "", participant_tz: tz,
  zoom_link: "", notes: "", phone: "", status: "scheduled",
});

export default function AdminMeetings({ csrf, meetings, reloadMeetings, onOpenLead, leads }) {
  const tzList = useMemo(() => {
    try {
      const all = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(all) && all.length) return all;
    } catch (e) { /* older browser */ }
    return FALLBACK_TZS;
  }, []);
  const localTz = useMemo(() => {
    let detected = "Europe/Berlin";
    try { detected = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin"; }
    catch (e) { /* older browser */ }
    return tzList.includes(detected) ? detected : "Europe/Berlin";
  }, [tzList]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(localTz));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [pendingStatus, setPendingStatus] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const suggestions = useMemo(() => {
    const q = (form.email || "").trim().toLowerCase();
    if (q.length < 2) return [];
    const seen = new Set();
    return (leads || [])
      .filter((l) => (l.email || "").toLowerCase().includes(q))
      .map((l) => ({ email: l.email, idx: (l.email || "").toLowerCase().indexOf(q) }))
      .sort((a, b) => a.idx - b.idx || a.email.length - b.email.length || a.email.localeCompare(b.email))
      .filter((s) => {
        const k = s.email.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, 8)
      .map((s) => s.email);
  }, [form.email, leads]);

  const selectSuggestion = (email) => {
    set("email", email);
    setShowSuggest(false);
    setActiveIndex(-1);
  };

  const onEmailKeyDown = (e) => {
    if (!showSuggest || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowSuggest(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm(localTz));
    setError("");
    setShowSuggest(false);
    setActiveIndex(-1);
    setShowForm(true);
  };
  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      email: m.email || "", meeting_date: m.meeting_date || "", meeting_time: m.meeting_time || "",
      participant_tz: m.participant_tz || localTz, zoom_link: m.zoom_link || "",
      notes: m.notes || "", phone: m.phone || "", status: m.status || "scheduled",
    });
    setError("");
    setShowSuggest(false);
    setActiveIndex(-1);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setError(""); setShowSuggest(false); setActiveIndex(-1); };

  const persist = async (body, id) => {
    const cfg = { headers: { "X-CSRF-Token": csrf }, withCredentials: true };
    if (id) return axios.put(`${API}/admin/meetings/${id}`, body, cfg);
    return axios.post(`${API}/admin/meetings`, body, cfg);
  };

  const submit = async () => {
    if (!form.email.trim()) { setError("Email is required."); return; }
    if (!form.meeting_date || !form.meeting_time) { setError("Meeting date and time are required."); return; }
    if (!form.participant_tz) { setError("Participant timezone is required."); return; }
    setSaving(true);
    setError("");
    try {
      await persist({
        email: form.email.trim(),
        meeting_date: form.meeting_date,
        meeting_time: form.meeting_time,
        participant_tz: form.participant_tz,
        zoom_link: form.zoom_link.trim(),
        notes: form.notes.slice(0, 500),
        phone: form.phone.trim().slice(0, 40),
        status: form.status,
      }, editingId);
      await reloadMeetings();
      closeForm();
    } catch (e) {
      setError(adminErrorDetail(e, "Saving failed. Log out and back in, then try again."));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (m, status) => {
    setStatusError("");
    // Optimistic: show the new value + colour immediately on the closed control.
    setPendingStatus((p) => ({ ...p, [m.id]: status }));
    const clear = () => setPendingStatus((p) => { const n = { ...p }; delete n[m.id]; return n; });
    try {
      await persist({
        email: m.email, meeting_date: m.meeting_date, meeting_time: m.meeting_time,
        participant_tz: m.participant_tz, zoom_link: m.zoom_link || "", notes: m.notes || "",
        phone: m.phone || "", status,
      }, m.id);
      await reloadMeetings();
      clear(); // reloaded m.status now equals the new value
    } catch (e) {
      // Save failed: revert the displayed value AND its colour, and surface the reason.
      clear();
      const detail = adminErrorDetail(e, "please try again");
      setStatusError(`Could not update status for ${m.email} — it was not saved. ${detail}`);
    }
  };

  const deleteMeeting = async (m) => {
    setDeleting(true);
    setStatusError("");
    try {
      await axios.delete(`${API}/admin/meetings/${m.id}`, { headers: { "X-CSRF-Token": csrf }, withCredentials: true });
      setConfirmDeleteId(null);
      await reloadMeetings();
    } catch (e) {
      const detail = adminErrorDetail(e, "please try again");
      setStatusError(`Could not delete the meeting for ${m.email}. ${detail}`);
    } finally {
      setDeleting(false);
    }
  };

  const list = meetings || [];

  const stats = useMemo(() => {
    const total = list.length;
    const completed = list.filter((m) => m.status === "completed").length;
    const noShow = list.filter((m) => m.status === "no_show").length;
    const canceled = list.filter((m) => m.status === "cancelled").length;
    const scheduled = list.filter((m) => m.status === "scheduled").length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, noShow, canceled, scheduled, completionRate };
  }, [list]);

  const q = search.trim();
  const qLower = q.toLowerCase();
  const qDigits = q.replace(/\D/g, "");
  const filtered = !q
    ? list
    : list.filter((m) => {
        const emailMatch = (m.email || "").toLowerCase().includes(qLower);
        const phoneMatch = qDigits.length > 0 && (m.phone || "").replace(/\D/g, "").includes(qDigits);
        return emailMatch || phoneMatch;
      });

  return (
    <div data-testid="table-meetings">
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <h2 className="font-display text-2xl" style={{ fontWeight: 800 }}>Meetings</h2>
        <button
          onClick={showForm && !editingId ? closeForm : openAdd}
          className="label-mono border border-ink text-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors"
          data-testid="meeting-add-toggle"
        >
          {showForm && !editingId ? "Close" : "Add meeting"}
        </button>
      </div>

      {showForm && (
        <div className="mb-8 border border-hairline p-4" data-testid="meeting-form">
          <div className="label-mono text-muted mb-3">{editingId ? "Edit meeting" : "Add meeting"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col relative">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-email">Email *</label>
              <input id="meeting-email" type="email" value={form.email}
                onChange={(e) => { set("email", e.target.value); setShowSuggest(true); setActiveIndex(-1); }}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 120)}
                onKeyDown={onEmailKeyDown}
                autoComplete="off"
                placeholder="name@example.com"
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="meeting-email" />
              {showSuggest && suggestions.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-hairline shadow-sm max-h-64 overflow-y-auto"
                  data-testid="meeting-email-suggestions">
                  {suggestions.map((s, si) => (
                    <li key={s}>
                      <button type="button"
                        onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                        onMouseEnter={() => setActiveIndex(si)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${si === activeIndex ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
                        data-testid={`meeting-email-suggestion-${si}`}>
                        {s}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex flex-col">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-phone">Phone</label>
              <input id="meeting-phone" type="text" value={form.phone} maxLength={40}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="optional"
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="meeting-phone" />
            </div>
            <div className="flex flex-col">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-tz">Participant timezone</label>
              <select id="meeting-tz" value={form.participant_tz} onChange={(e) => set("participant_tz", e.target.value)}
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="meeting-tz">
                {tzList.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-date">Meeting date (participant local)</label>
              <input id="meeting-date" type="date" value={form.meeting_date} onChange={(e) => set("meeting_date", e.target.value)}
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="meeting-date" />
            </div>
            <div className="flex flex-col">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-time">Meeting time (participant local)</label>
              <input id="meeting-time" type="time" value={form.meeting_time} onChange={(e) => set("meeting_time", e.target.value)}
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="meeting-time" />
            </div>
            <div className="flex flex-col md:col-span-2">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-zoom">Zoom link</label>
              <input id="meeting-zoom" type="text" value={form.zoom_link} onChange={(e) => set("zoom_link", e.target.value)}
                placeholder="https://zoom.us/j/…"
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                data-testid="meeting-zoom" />
            </div>
            <div className="flex flex-col md:col-span-2">
              <label className="label-mono text-muted mb-1" htmlFor="meeting-notes">Notes ({form.notes.length}/500)</label>
              <textarea id="meeting-notes" value={form.notes} maxLength={500} rows={3}
                onChange={(e) => set("notes", e.target.value)}
                className="bg-transparent border border-hairline focus:border-ink py-1 px-2 text-sm outline-none resize-y"
                data-testid="meeting-notes" />
            </div>
            {editingId && (
              <div className="flex flex-col">
                <label className="label-mono text-muted mb-1" htmlFor="meeting-status">Status</label>
                <select id="meeting-status" value={form.status} onChange={(e) => set("status", e.target.value)}
                  className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none"
                  data-testid="meeting-status-select">
                  {STATUS_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            )}
          </div>
          {error && <p className="mt-3 text-sm text-red-600" data-testid="meeting-form-error">{error}</p>}
          <div className="mt-4 flex items-center gap-3">
            <button onClick={submit} disabled={saving}
              className="label-mono border border-ink text-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors disabled:opacity-40"
              data-testid="meeting-save">
              {saving ? "Saving…" : editingId ? "Save changes" : "Add meeting"}
            </button>
            <button onClick={closeForm}
              className="label-mono text-muted hover:text-ink transition-colors" data-testid="meeting-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {statusError && (
        <p className="mb-4 text-sm text-red-600" data-testid="meeting-status-error">{statusError}</p>
      )}
      <div className="mb-8 grid grid-cols-2 md:grid-cols-6 gap-px bg-hairline border border-hairline" data-testid="meetings-summary">
        {[
          { key: "total", label: "Total", value: stats.total, tid: "meetings-stat-total" },
          { key: "completed", label: "Completed", value: stats.completed, tid: "meetings-stat-completed" },
          { key: "no_show", label: "No show", value: stats.noShow, tid: "meetings-stat-no-show" },
          { key: "canceled", label: "Canceled", value: stats.canceled, tid: "meetings-stat-canceled" },
          { key: "scheduled", label: "Scheduled / upcoming", value: stats.scheduled, tid: "meetings-stat-scheduled" },
          { key: "rate", label: "Completion rate", value: `${stats.completionRate}%`, tid: "meetings-stat-completion-rate" },
        ].map((s) => (
          <div key={s.key} className="bg-white px-4 py-3" data-testid={s.tid}>
            <div className="label-mono text-muted mb-1">{s.label}</div>
            <div className="font-display text-2xl text-ink" style={{ fontWeight: 800 }}>{s.value}</div>
          </div>
        ))}
      </div>
      {list.length === 0 ? (
        <p className="text-muted" data-testid="meetings-empty">No meetings yet.</p>
      ) : (
        <>
          <div className="relative w-64 mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or phone..."
              className="bg-transparent border-b border-hairline focus:border-ink py-1 pr-6 text-sm outline-none w-64"
              data-testid="meetings-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-sm leading-none px-1"
                aria-label="Clear search"
                data-testid="meetings-search-clear"
              >
                ×
              </button>
            )}
          </div>
          <div className="overflow-x-auto border border-hairline">
          <table className="w-full text-sm border-collapse" data-testid="meetings-table">
            <thead>
              <tr className="label-mono text-muted border-b border-hairline">
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Date</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Timezone</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Local time</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Berlin time</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Email</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Phone</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Zoom</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Status</th>
                <th className="text-left font-normal py-3 px-4 whitespace-nowrap">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {filtered.map((m) => {
                const delta = dayDelta(m);
                return (
                  <tr key={m.id} className="align-middle" data-testid={`meeting-row-${m.id}`}>
                    <td className="py-3 px-4 whitespace-nowrap text-ink" data-testid={`meeting-date-${m.id}`}>{fmtDate(m.meeting_date)}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-muted label-mono">{m.participant_tz || "—"}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-ink">{m.meeting_time || "—"}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-ink" data-testid={`meeting-berlin-${m.id}`}>
                      {m.berlin_time || "—"}
                      {delta !== 0 && m.berlin_time && (
                        <span className="label-mono text-muted ml-1">({delta > 0 ? "+" : "−"}{Math.abs(delta)} day{Math.abs(delta) > 1 ? "s" : ""})</span>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {m.lead_id ? (
                        <button onClick={() => onOpenLead && onOpenLead(m.email)}
                          className="inline-block px-2 py-0.5 text-sm rounded underline underline-offset-2 transition-colors"
                          style={{ backgroundColor: "#EDF1F6", border: "1px solid #CBD8E5", color: "#2C4A63" }}
                          data-testid={`meeting-email-link-${m.id}`}>
                          {m.email}
                        </button>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-sm rounded"
                          style={{ backgroundColor: "#EDF1F6", border: "1px solid #CBD8E5", color: "#2C4A63" }}
                          data-testid={`meeting-email-${m.id}`}>
                          {m.email}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap" data-testid={`meeting-phone-cell-${m.id}`}>
                      {m.phone ? (
                        <span className="inline-block px-2 py-0.5 text-sm rounded"
                          style={{ backgroundColor: "#EDF1F6", border: "1px solid #CBD8E5", color: "#2C4A63" }}>
                          {m.phone}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {m.zoom_link ? (
                        <a href={m.zoom_link} target="_blank" rel="noopener noreferrer"
                          className="text-ink hover:text-leaf transition-colors" data-testid={`meeting-zoom-${m.id}`}>Zoom ↗</a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {(() => {
                        const shown = pendingStatus[m.id] !== undefined ? pendingStatus[m.id] : m.status;
                        return (
                          <select value={shown} onChange={(e) => changeStatus(m, e.target.value)}
                            style={STATUS_STYLE[shown] || undefined}
                            className="border border-hairline focus:border-ink py-1 px-1 text-sm outline-none"
                            data-testid={`meeting-status-${m.id}`}>
                            {STATUS_OPTIONS.map((s) => <option key={s.key} value={s.key} style={OPTION_STYLE}>{s.label}</option>)}
                          </select>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      {confirmDeleteId === m.id ? (
                        <div className="flex flex-wrap items-center gap-2" data-testid={`meeting-delete-confirm-${m.id}`}>
                          <span className="label-mono text-muted">Delete {fmtDate(m.meeting_date)} · {m.email}?</span>
                          <button onClick={() => deleteMeeting(m)} disabled={deleting}
                            className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors disabled:opacity-40"
                            data-testid={`meeting-delete-confirm-yes-${m.id}`}>
                            {deleting ? "Deleting…" : "Confirm delete"}
                          </button>
                          <button onClick={() => setConfirmDeleteId(null)}
                            className="label-mono text-muted hover:text-ink transition-colors"
                            data-testid={`meeting-delete-cancel-${m.id}`}>Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <button onClick={() => openEdit(m)}
                            className="label-mono border border-hairline px-2 py-0.5 hover:bg-ink hover:text-white transition-colors"
                            data-testid={`meeting-edit-${m.id}`}>Edit</button>
                          <button onClick={() => { setStatusError(""); setConfirmDeleteId(m.id); }}
                            className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                            data-testid={`meeting-delete-${m.id}`}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {filtered.length === 0 && (
            <p className="text-muted mt-4" data-testid="meetings-no-results">No meetings match your search.</p>
          )}
        </>
      )}
    </div>
  );
}
